import { useState, useMemo } from "react";
import api from "@/utils/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Bell, FileText, Send, CheckCircle2, XCircle, Loader2, Euro } from "lucide-react";

// Spiegel zu backend invoice_dunning_service.DUNNING_LEVELS
// (Level, Bezeichnung, Gebühr €, Schwelle Tage nach Fälligkeit)
const DUNNING_LEVELS = [
  [1, "Zahlungserinnerung", 0.0, 7],
  [2, "1. Mahnung", 5.0, 14],
  [3, "2. Mahnung", 10.0, 30],
];

const fmtEur = (v) =>
  new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(v || 0);

const fmtDate = (v) => {
  if (!v) return "—";
  try {
    return new Date(v).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });
  } catch {
    return "—";
  }
};

export default function DunningModal({ invoice, open, onClose, onSent }) {
  const [recipient, setRecipient] = useState(invoice?.customer_email || "");
  const [sending, setSending] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [chargeFee, setChargeFee] = useState(true);
  const [includeQr, setIncludeQr] = useState(true);

  const daysOverdue = useMemo(() => {
    if (!invoice?.due_date) return 0;
    const diff = Math.floor((Date.now() - new Date(invoice.due_date).getTime()) / 86400000);
    return Math.max(0, diff);
  }, [invoice]);

  const history = invoice?.dunning_history || [];
  const currentLevel = invoice?.dunning_level || 0;
  const feesPaid = history.reduce((s, h) => s + (h.fee || 0), 0);
  const total = invoice?.total || invoice?.total_amount || invoice?.subtotal || 0;

  const nextLevel = useMemo(() => {
    const cfg = DUNNING_LEVELS.find(([lvl]) => lvl === currentLevel + 1);
    if (!cfg) return null;
    const [level, label, fee, threshold] = cfg;
    return { level, label, fee, threshold, available: daysOverdue >= threshold };
  }, [currentLevel, daysOverdue]);

  const effectiveFee = chargeFee ? (nextLevel?.fee || 0) : 0;
  const amountDue = total + feesPaid + effectiveFee;

  const handlePreview = async () => {
    setPreviewing(true);
    try {
      const res = await api.get(
        `/invoices/${invoice.id}/dunning/pdf?qr=${includeQr}&fee=${chargeFee}`,
        { responseType: "blob" }
      );
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      window.open(url, "_blank");
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Vorschau konnte nicht erstellt werden.");
    } finally {
      setPreviewing(false);
    }
  };

  const handleSend = async () => {
    if (!recipient || !recipient.includes("@")) {
      toast.error("Bitte eine gültige E-Mail-Adresse angeben.");
      return;
    }
    setSending(true);
    try {
      const res = await api.post(`/invoices/${invoice.id}/dunning`, {
        recipient_email: recipient,
        charge_fee: chargeFee,
        include_qr: includeQr,
      });
      const d = res.data;
      if (d.email_sent) {
        toast.success(`${d.label} an ${recipient} versendet.`);
      } else {
        toast.warning(`${d.label} erstellt — E-Mail-Versand fehlgeschlagen (Empfänger prüfen). Mahnstufe wurde dennoch gesetzt.`);
      }
      onSent?.();
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Mahnung konnte nicht versendet werden.");
    } finally {
      setSending(false);
    }
  };

  if (!invoice) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-lg" data-testid="dunning-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5 text-amber-600" />
            Mahnwesen — {invoice.invoice_number}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Eckdaten */}
          <div className="grid grid-cols-2 gap-3 rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm">
            <div>
              <p className="text-xs text-slate-500">Rechnungsbetrag</p>
              <p className="font-mono font-semibold text-slate-900" data-testid="dunning-total">{fmtEur(total)}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500">Fällig seit</p>
              <p className="font-medium text-slate-900">{fmtDate(invoice.due_date)}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500">Überfällig</p>
              <p className={`font-medium ${daysOverdue > 0 ? "text-rose-600" : "text-slate-900"}`} data-testid="dunning-days-overdue">
                {daysOverdue} Tag{daysOverdue === 1 ? "" : "e"}
              </p>
            </div>
            <div>
              <p className="text-xs text-slate-500">Aktuelle Stufe</p>
              <p className="font-medium text-slate-900">
                {currentLevel > 0 ? (DUNNING_LEVELS.find(([l]) => l === currentLevel)?.[1] || "—") : "Noch nicht gemahnt"}
              </p>
            </div>
          </div>

          {/* Historie */}
          {history.length > 0 && (
            <div className="space-y-1.5" data-testid="dunning-history">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Mahn-Historie</p>
              {history.map((h, i) => (
                <div key={i} className="flex items-center justify-between rounded-md border border-slate-100 px-3 py-2 text-sm">
                  <div className="flex items-center gap-2">
                    {h.email_sent ? (
                      <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                    ) : (
                      <XCircle className="h-4 w-4 text-amber-500" />
                    )}
                    <span className="text-slate-700">{h.label}</span>
                    <span className="text-xs text-slate-400">{fmtDate(h.sent_at)}</span>
                  </div>
                  <span className="font-mono text-xs text-slate-500">{h.fee > 0 ? `+${fmtEur(h.fee)}` : "—"}</span>
                </div>
              ))}
            </div>
          )}

          {/* Nächste Stufe */}
          {nextLevel ? (
            nextLevel.available ? (
              <div className="space-y-3 rounded-lg border border-amber-200 bg-amber-50 p-4" data-testid="dunning-next-available">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs uppercase tracking-wide text-amber-700">Nächste Mahnstufe</p>
                    <p className="font-semibold text-amber-900">{nextLevel.label}</p>
                  </div>
                  <Badge variant="secondary" className="bg-amber-100 text-amber-800">
                    Mahngebühr {nextLevel.fee > 0 ? fmtEur(nextLevel.fee) : "kostenfrei"}
                  </Badge>
                </div>

                {/* Optionen: Mahngebühr + Zahlungs-QR */}
                <div className="space-y-2 rounded-md bg-white px-3 py-2.5">
                  {nextLevel.fee > 0 && (
                    <label className="flex items-center gap-2.5 text-sm text-slate-700 cursor-pointer" data-testid="dunning-charge-fee-row">
                      <Checkbox
                        checked={chargeFee}
                        onCheckedChange={(v) => setChargeFee(!!v)}
                        data-testid="dunning-charge-fee-checkbox"
                      />
                      Mahngebühr von {fmtEur(nextLevel.fee)} erheben
                    </label>
                  )}
                  <label className="flex items-center gap-2.5 text-sm text-slate-700 cursor-pointer" data-testid="dunning-qr-row">
                    <Checkbox
                      checked={includeQr}
                      onCheckedChange={(v) => setIncludeQr(!!v)}
                      data-testid="dunning-qr-checkbox"
                    />
                    Zahlungs-QR-Code aufs PDF (zum Bezahlen per Banking-App)
                  </label>
                </div>

                <div className="flex items-center justify-between rounded-md bg-white px-3 py-2">
                  <span className="flex items-center gap-1.5 text-sm text-slate-600"><Euro className="h-4 w-4" /> Offener Gesamtbetrag</span>
                  <span className="font-mono font-bold text-amber-900" data-testid="dunning-amount-due">{fmtEur(amountDue)}</span>
                </div>
                <div>
                  <Label htmlFor="dunning-recipient" className="text-xs text-slate-600">Empfänger-E-Mail</Label>
                  <Input
                    id="dunning-recipient"
                    type="email"
                    value={recipient}
                    onChange={(e) => setRecipient(e.target.value)}
                    placeholder="kunde@example.com"
                    className="mt-1"
                    data-testid="dunning-recipient-input"
                  />
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={handlePreview}
                    disabled={previewing}
                    className="flex-1"
                    data-testid="dunning-preview-btn"
                  >
                    {previewing ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <FileText className="h-4 w-4 mr-1" />}
                    Vorschau (PDF)
                  </Button>
                  <Button
                    onClick={handleSend}
                    disabled={sending}
                    className="flex-1 bg-amber-600 hover:bg-amber-700"
                    data-testid="dunning-send-btn"
                  >
                    {sending ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Send className="h-4 w-4 mr-1" />}
                    {nextLevel.label} senden
                  </Button>
                </div>
              </div>
            ) : (
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600" data-testid="dunning-not-due">
                Die nächste Stufe <strong>{nextLevel.label}</strong> wird ab <strong>{nextLevel.threshold} Tagen</strong> Überfälligkeit fällig
                (aktuell {daysOverdue} Tag{daysOverdue === 1 ? "" : "e"}). Noch {nextLevel.threshold - daysOverdue} Tag(e) warten.
              </div>
            )
          ) : (
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600" data-testid="dunning-max-reached">
              Alle Mahnstufen wurden versendet. Für weitere Schritte (gerichtliches Mahnverfahren / Inkasso) wende dich an deinen Rechtsbeistand.
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
